import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ClientDto, ClientInput, ContactDto, DealDto, GuardSiteDto, InvoiceDto, QuoteDto, TicketDto, VisitDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { useNavState } from '../navState';
import { Icon } from '../icons';
import { euro, fmtDate, fmtDateTime, parseEuro } from '../format';
import { Empty, ErrorBanner, Field, Modal, Panel } from '../ui';
import { DealFiles } from './DealFiles';
import { DealModal } from './DealsPage';
import { ContactModal } from './ContactsPage';
import type { Api } from '../api';
import { WhatsAppButton, waNumber } from '../whatsapp';
import { mailTermFor, searchMail, type FoundMail } from '../mailSearch';

/**
 * Aziende: un solo elenco per potenziali clienti, trattative, clienti e rubrica.
 * Aprendo un'azienda si vede tutto quello che la riguarda: persone, preventivi, documenti, visite, fatture, assistenza.
 * Unisce i dati che già ci sono (trattative, rubrica, clienti), senza cambiare il database.
 */

export type Cat = 'potenziale' | 'trattativa' | 'cliente' | 'perso' | 'contatto';
export const CAT: Record<Cat, { label: string; cls: string }> = {
  potenziale: { label: 'Potenziale', cls: 'info' },
  trattativa: { label: 'In trattativa', cls: 'warn' },
  cliente: { label: 'Cliente', cls: 'good' },
  perso: { label: 'Persa', cls: 'bad' },
  contatto: { label: 'Solo rubrica', cls: '' },
};
const FILTERS: { v: Cat | 'tutte'; label: string; hint: string }[] = [
  { v: 'tutte', label: 'Tutte', hint: 'Clienti, potenziali e trattative (senza le perse e i soli contatti)' },
  { v: 'potenziale', label: 'Potenziali', hint: 'Aziende da contattare o appena contattate' },
  { v: 'trattativa', label: 'In trattativa', hint: 'Hanno un preventivo in corso' },
  { v: 'cliente', label: 'Clienti', hint: 'Hanno già firmato' },
  { v: 'contatto', label: 'Altri contatti', hint: 'Persone e aziende della rubrica senza trattativa' },
];
const QUOTE_LABEL: Record<string, string> = { draft: 'Bozza', sent: 'Inviato', accepted: 'Accettato', rejected: 'Rifiutato', expired: 'Scaduto' };
const INVOICE_LABEL: Record<string, string> = { draft: 'Bozza', issued: 'Emessa', sent: 'Inviata', paid: 'Pagata' };
const TICKET_LABEL: Record<string, string> = { open: 'Aperta', in_progress: 'In lavorazione', resolved: 'Risolta' };

export interface Company {
  key: string;
  name: string;
  cat: Cat;
  deal: DealDto | null;
  clientId: number | null;
  clientName: string | null;
  contacts: ContactDto[];
  quotes: QuoteDto[];
  visits: VisitDto[];
  invoices: InvoiceDto[];
  tickets: TicketDto[];
}

/** Nome senza forma societaria, accenti e punteggiatura: «Carves Packaging S.r.l.» = «carves packaging». */
export const norm = (s: string | null | undefined) =>
  (s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/s\.?\s?r\.?\s?l\.?\s?s?\.?|s\.?\s?p\.?\s?a\.?|s\.?\s?a\.?\s?s\.?|s\.?\s?n\.?\s?c\.?/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Chiave per riconoscere la stessa azienda scritta in modi diversi (23/10/2026, Marco):
 * senza le note tra parentesi e senza spazi. «Manifatture Montecristo S.r.l. (calzaturificio)»
 * = «Manifatture Monte Cristo» = «Manifatture Montecristo S.r.l.».
 */
export const companyKey = (s: string | null | undefined) => norm((s ?? '').replace(/\([^)]*\)/g, ' ')).replace(/ /g, '');

function catOf(d: DealDto): Cat {
  if (d.phase === 'lost' || d.stage === 'lost') return 'perso';
  if (d.clientId || d.stage === 'won' || d.phase === 'client') return 'cliente';
  if (d.phase === 'quote' || d.stage === 'quote') return 'trattativa';
  return 'potenziale';
}

export interface Data {
  deals: DealDto[];
  contacts: ContactDto[];
  clients: { id: number; name: string }[];
  quotes: QuoteDto[];
  visits: VisitDto[];
  invoices: InvoiceDto[];
  tickets: TicketDto[];
}

export function build(d: Data): Company[] {
  const list: Company[] = [];
  const byNorm = new Map<string, Company>();
  const add = (c: Company) => {
    list.push(c);
    const n = companyKey(c.name);
    if (n && !byNorm.has(n)) byNorm.set(n, c);
    return c;
  };
  const blank = (key: string, name: string, cat: Cat): Company => ({
    key, name, cat, deal: null, clientId: null, clientName: null, contacts: [], quotes: [], visits: [], invoices: [], tickets: [],
  });

  for (const deal of d.deals) add({ ...blank(`d${deal.id}`, deal.name, catOf(deal)), deal, clientId: deal.clientId });
  for (const cl of d.clients) {
    const own = list.find((c) => c.clientId === cl.id) ?? byNorm.get(companyKey(cl.name));
    if (own) {
      own.clientId = cl.id;
      own.clientName = cl.name;
      if (own.cat !== 'perso') own.cat = 'cliente';
    } else add({ ...blank(`c${cl.id}`, cl.name, 'cliente'), clientId: cl.id, clientName: cl.name });
  }
  const byClient = (id: number | null | undefined) => (id ? list.find((c) => c.clientId === id) : undefined);
  const byDeal = (id: number | null | undefined) => (id ? list.find((c) => c.deal?.id === id) : undefined);

  // Persone e aziende della rubrica: vanno sotto l'azienda giusta; quelle senza azienda diventano «Altri contatti»
  for (const p of d.contacts) {
    const target =
      list.find((c) => c.deal?.contactId === p.id) ??
      byClient(p.clientId) ??
      byNorm.get(companyKey(p.company)) ??
      byNorm.get(companyKey(p.name)) ??
      add(blank(`r${p.id}`, p.company || p.name, 'contatto'));
    target.contacts.push(p);
  }
  for (const q of d.quotes) (byDeal(q.dealId) ?? byClient(q.clientId) ?? byNorm.get(companyKey(q.recipientName)))?.quotes.push(q);
  for (const v of d.visits) {
    const t = byDeal(v.dealId) ?? byClient(v.clientId) ?? (v.contactId ? list.find((c) => c.contacts.some((p) => p.id === v.contactId)) : undefined);
    t?.visits.push(v);
  }
  for (const i of d.invoices) (byClient(i.clientId) ?? byNorm.get(companyKey(i.recipientName)))?.invoices.push(i);
  for (const t of d.tickets) byClient(t.clientId)?.tickets.push(t);
  return list.sort((a, b) => a.name.localeCompare(b.name, 'it'));
}

/** Persona principale: quella collegata alla trattativa, oppure quella con P.IVA o indirizzo. */
export const mainContact = (c: Company) =>
  c.contacts.find((p) => p.id === c.deal?.contactId) ?? c.contacts.find((p) => p.vat || p.address) ?? c.contacts[0] ?? null;

type Tab = 'riepilogo' | 'persone' | 'preventivi' | 'documenti' | 'email' | 'visite' | 'fatture' | 'assistenza';

/** Tutti gli elenchi che servono per costruire le aziende; quelli non permessi (es. commerciale) restano vuoti. */
export async function loadCompanyData(api: Api): Promise<Data> {
  const get = async <T,>(url: string): Promise<T[]> => {
    try {
      return await api.get<T[]>(url);
    } catch {
      return [];
    }
  };
  const [deals, contacts, clients, quotes, visits, invoices, tickets] = await Promise.all([
    api.get<DealDto[]>('/api/deals'),
    get<ContactDto>('/api/contacts'),
    get<{ id: number; name: string }>('/api/invoices/meta/clients'),
    get<QuoteDto>('/api/quotes'),
    get<VisitDto>('/api/visits'),
    get<InvoiceDto>('/api/invoices'),
    get<TicketDto>('/api/tickets'),
  ]);
  return { deals, contacts, clients, quotes, visits, invoices, tickets };
}

export function CompaniesPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const isAdmin = ctx.me.user.role === 'admin';
  const selKey = `tt.company.${ctx.me.user.id}`;
  const [data, setData] = useState<Data | null>(null);
  const [monitorSites, setMonitorSites] = useState<GuardSiteDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Arrivando dalla ricerca generale, la lista a sinistra mostra la stessa ricerca
  const [q, setQ] = useState(() => { const v = (ctx.pageParam as { q?: unknown } | null)?.q; return typeof v === 'string' ? v.slice(0, 100) : ''; });
  const [filter, setFilter] = useState<Cat | 'tutte'>(() => (localStorage.getItem(`${selKey}.filter`) as Cat | 'tutte' | null) ?? 'tutte');
  const [selected, setSelectedNav] = useNavState<string | null>(ctx, 'company',
    // Aperta da un'altra pagina (ricerca, Oggi): setPage('companies', { company: chiave })
    typeof (ctx.pageParam as { company?: unknown } | null)?.company === 'string' ? (ctx.pageParam as { company: string }).company : null);
  // Persona cercata (ricerca): si apre l'azienda sulla scheda Persone con la sua scheda già aperta
  const askedPerson = useRef<number | null>(typeof (ctx.pageParam as { person?: unknown } | null)?.person === 'number' ? (ctx.pageParam as { person: number }).person : null);
  const [tab, setTab] = useState<Tab>(askedPerson.current != null ? 'persone' : 'riepilogo');
  const [dealOpen, setDealOpen] = useState<DealDto | null>(null);
  const [person, setPerson] = useState<ContactDto | 'new' | null>(null);
  const [becoming, setBecoming] = useState<Company | null>(null);

  const setSelected = (k: string | null) => {
    setSelectedNav(k);
    setTab('riepilogo');
  };
  const setFilterSaved = (v: Cat | 'tutte') => {
    localStorage.setItem(`${selKey}.filter`, v);
    setFilter(v);
  };

  const load = useCallback(async () => {
    try {
      setData(await loadCompanyData(api));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (askedPerson.current == null || !data) return;
    const found = data.contacts.find((p) => p.id === askedPerson.current);
    askedPerson.current = null;
    if (found) setPerson(found);
  }, [data]);

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    void api.get<{ sites: GuardSiteDto[] }>('/api/guard/sites')
      .then((result) => { if (alive) setMonitorSites(result.sites); })
      .catch(() => { if (alive) setMonitorSites([]); });
    return () => { alive = false; };
  }, [api, isAdmin]);

  const companies = useMemo(() => (data ? build(data) : []), [data]);
  const counts = useMemo(() => {
    const m: Record<string, number> = { tutte: 0 };
    for (const c of companies) {
      m[c.cat] = (m[c.cat] ?? 0) + 1;
      if (c.cat !== 'perso' && c.cat !== 'contatto') m.tutte++;
    }
    return m;
  }, [companies]);
  const s = norm(q);
  const visible = companies.filter((c) => {
    if (filter === 'tutte' ? c.cat === 'perso' || c.cat === 'contatto' : c.cat !== filter) return !!s && hay(c).includes(s);
    return !s || hay(c).includes(s);
  });
  const current = companies.find((c) => c.key === selected) ?? null;

  // Sul telefono si vede una cosa alla volta: aprendo un'azienda la scheda parte dall'alto
  useEffect(() => {
    if (!current || !window.matchMedia('(max-width: 980px)').matches) return;
    document.querySelector('.page')?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [current?.key]);

  return (
    <div className={`panels co-panels${current ? ' co-has-sel' : ''}`}>
      <Panel
        title="Aziende"
        className="co-list"
        subtitle={`${counts.tutte} aziende`}
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            <button type="button" className="btn small ghost" title="Tutte le persone e le aziende in un elenco semplice" onClick={() => ctx.setPage('contacts')}>
              <Icon name="user" size={15} /> Rubrica
            </button>
            {isAdmin && (
              <button type="button" className="btn small ghost" title="Clienti con tariffe, ore e accesso all'area riservata" onClick={() => ctx.setPage('clients')}>
                <Icon name="gear" size={15} /> Tariffe
              </button>
            )}
            <button type="button" className="btn small dark" title="Aggiungi un’azienda nuova: parte come segnalazione nel Percorso" onClick={() => ctx.setPage('deals', { newLead: Date.now() })}>
              <Icon name="plus" size={15} /> Nuova
            </button>
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <input className="deal-search co-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca azienda, persona, P.IVA…" />
        <div className="ag-filter co-filter">
          {FILTERS.map((f) => (
            <button key={f.v} type="button" className={filter === f.v ? 'on' : ''} title={f.hint} onClick={() => setFilterSaved(f.v)}>
              {f.label} <span className="co-n">{counts[f.v] ?? 0}</span>
            </button>
          ))}
        </div>
        {!data ? (
          <div className="muted small">Caricamento…</div>
        ) : visible.length === 0 ? (
          <Empty icon="building">Nessuna azienda trovata.</Empty>
        ) : (
          <div className="ag-list">
            {visible.map((c) => (
              <div key={c.key} className={`ag-row co-row${c.key === selected ? ' on' : ''}`} onClick={() => setSelected(c.key)}>
                <span className={`co-dot ${CAT[c.cat].cls}`} />
                <span className="ag-title">
                  <b>{c.name}</b>
                  <div className="muted small">
                    {CAT[c.cat].label}
                    {c.deal?.valueCents ? ` · ${euro(c.deal.valueCents)}` : ''}
                    {c.deal?.source ? ` · ${c.deal.source}` : ''}
                  </div>
                </span>
                {c.quotes.length > 0 && (
                  <span className="co-mini" title="Preventivi fatti">
                    <Icon name="note" size={13} /> {c.quotes.length}
                  </span>
                )}
                <Icon name="chevronRight" size={16} className="muted" />
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel
        title={current ? current.name : 'Scheda azienda'}
        className="co-card"
        right={current ? (
          <button type="button" className="btn small ghost co-back" title="Torna all'elenco delle aziende" onClick={() => setSelected(null)}>
            <Icon name="chevronLeft" size={14} /> Aziende
          </button>
        ) : undefined}
      >
        {!current ? (
          <Empty icon="building">Scegli un'azienda a sinistra: qui trovi persone, preventivi, documenti, visite e fatture.</Empty>
        ) : (
          <CompanyCard
            ctx={ctx}
            c={current}
            isAdmin={isAdmin}
            monitorSiteId={monitorSites.find((site) => site.clientId != null && site.clientId === current.clientId)?.id ?? null}
            tab={tab}
            onTab={setTab}
            onEditDeal={() => current.deal && setDealOpen(current.deal)}
            onPerson={setPerson}
            onBecomeClient={() => setBecoming(current)}
          />
        )}
      </Panel>

      {dealOpen && (
        <DealModal
          ctx={ctx}
          deal={dealOpen}
          onClose={() => setDealOpen(null)}
          onSaved={() => {
            setDealOpen(null);
            void load();
          }}
        />
      )}
      {becoming && (
        <BecomeClientModal
          ctx={ctx}
          c={becoming}
          onClose={() => setBecoming(null)}
          onDone={() => {
            setBecoming(null);
            void load();
          }}
        />
      )}
      {person && current && (
        <ContactModal
          ctx={ctx}
          contact={person === 'new' ? null : person}
          prefill={person === 'new' ? { company: mainContact(current)?.company || current.name } : undefined}
          onDeal={() => {
            setPerson(null);
            if (current.deal) setDealOpen(current.deal);
          }}
          onQuote={() => {
            setPerson(null);
            newQuote(ctx, current);
          }}
          onClose={() => setPerson(null)}
          onSaved={() => {
            setPerson(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

const hay = (c: Company) =>
  norm(
    [c.name, c.clientName, c.deal?.contactName, c.deal?.source, ...c.contacts.flatMap((p) => [p.name, p.company, p.vat, p.email, p.phone])]
      .filter(Boolean)
      .join(' '),
  );

const newQuote = (ctx: Ctx, c: Company) =>
  ctx.setPage('quotes', { newFromDeal: { dealId: c.deal?.id ?? null, name: c.name, email: c.deal?.email ?? mainContact(c)?.email ?? '', value: c.deal?.valueCents ?? 0 } });

function CompanyCard({
  ctx,
  c,
  isAdmin,
  monitorSiteId,
  tab,
  onTab,
  onEditDeal,
  onPerson,
  onBecomeClient,
}: {
  ctx: Ctx;
  c: Company;
  isAdmin: boolean;
  monitorSiteId: number | null;
  tab: Tab;
  onTab: (t: Tab) => void;
  onEditDeal: () => void;
  onPerson: (p: ContactDto | 'new') => void;
  onBecomeClient: () => void;
}) {
  const main = mainContact(c);
  const d = c.deal;
  const tabs: { v: Tab; label: string; n?: number }[] = [
    { v: 'riepilogo', label: 'Riepilogo' },
    { v: 'persone', label: 'Persone', n: c.contacts.length },
    { v: 'preventivi', label: 'Preventivi', n: c.quotes.length },
    { v: 'documenti', label: 'Documenti' },
    { v: 'email', label: 'Email' },
    { v: 'visite', label: 'Visite', n: c.visits.length },
    { v: 'fatture', label: 'Fatture', n: c.invoices.length },
    { v: 'assistenza', label: 'Assistenza', n: c.tickets.length },
  ];
  const go = (page: string, param?: unknown) => ctx.setPage(page, param);

  return (
    <div className="co-body">
      <div className="co-head">
        <span className={`badge ${CAT[c.cat].cls}`}>{CAT[c.cat].label}</span>
        {d?.valueCents ? <span className="co-value">{euro(d.valueCents)}</span> : null}
        {d && <span className="muted small">segue {d.ownerName}</span>}
      </div>
      <div className="sec-tabs co-tabs" style={{ '--nc': '#8b5cf6' } as React.CSSProperties}>
        {tabs.map((t) => (
          <button key={t.v} type="button" className={tab === t.v ? 'on' : ''} onClick={() => onTab(t.v)}>
            {t.label}
            {t.n ? <span className="co-n">{t.n}</span> : null}
          </button>
        ))}
      </div>

      {tab === 'riepilogo' && (
        <>
          <div className="co-actions">
            {d ? (
              <button type="button" className="btn" title="Modifica la trattativa: fase, valore, prossima cosa da fare" onClick={onEditDeal}>
                <Icon name="edit" size={15} /> Trattativa
              </button>
            ) : (
              <button
                type="button"
                className="btn"
                title="Apri una trattativa per seguire questa azienda fino al contratto"
                onClick={() => go('deals', { newFromContact: { name: c.name, contactName: main && main.name !== c.name ? main.name : '', email: main?.email ?? '', phone: main?.phone ?? '' } })}
              >
                <Icon name="layers" size={15} /> Crea trattativa
              </button>
            )}
            <button type="button" className="btn" title="Prepara un preventivo per questa azienda" onClick={() => newQuote(ctx, c)}>
              <Icon name="note" size={15} /> Nuovo preventivo
            </button>
            <button type="button" className="btn" title="Fissa una visita o una call con questa azienda" onClick={() => go('visits', { newVisit: { dealId: d?.id ?? null, contactId: main?.id ?? null, clientId: c.clientId } })}>
              <Icon name="calendar" size={15} /> Fissa visita
            </button>
            {isAdmin && !c.clientId && c.cat !== 'perso' && (
              <button type="button" className="btn good" onClick={onBecomeClient} title="Ha firmato: passa tra i Clienti, già collegata a questa trattativa">
                <Icon name="check" size={15} /> Diventa cliente
              </button>
            )}
            {isAdmin && c.clientId && (
              <button type="button" className="btn ghost" title="Tariffa oraria del cliente e il suo accesso al CRM" onClick={() => go('clients', { open: c.clientId })}>
                <Icon name="gear" size={15} /> Tariffe e accesso
              </button>
            )}
            {isAdmin && monitorSiteId != null && (
              <button type="button" className="btn ghost" title="Questo cliente ha un monitoraggio di sicurezza: apre la sua area riservata" onClick={() => go('security', monitorSiteId)}>
                <Icon name="shield" size={15} /> Apri monitoraggio
              </button>
            )}
          </div>
          {d && (d.nextActionText || d.nextActionDate) && (
            <div className="co-next">
              <span className="eyebrow-s">Prossima cosa da fare</span>
              <b>{d.nextActionText || 'Da seguire'}</b>
              {d.nextActionDate && <span className="muted"> · {fmtDate(d.nextActionDate)}</span>}
            </div>
          )}
          <dl className="co-kv">
            {main?.vat && (
              <>
                <dt>P.IVA</dt>
                <dd>{main.vat}</dd>
              </>
            )}
            {main?.address && (
              <>
                <dt>Indirizzo</dt>
                <dd>{main.address}</dd>
              </>
            )}
            {(d?.phone || main?.phone) && (
              <>
                <dt>Telefono</dt>
                <dd>
                  <a href={`tel:${d?.phone || main?.phone}`}>{d?.phone || main?.phone}</a>
                  {waNumber(d?.phone || main?.phone) && (
                    <WhatsAppButton ctx={ctx} to={{ name: d?.contactName || main?.name || c.name, phone: (d?.phone || main?.phone)! }} />
                  )}
                </dd>
              </>
            )}
            {(d?.email || main?.email) && (
              <>
                <dt>Email</dt>
                <dd>
                  <a href={`mailto:${d?.email || main?.email}`}>{d?.email || main?.email}</a>
                </dd>
              </>
            )}
            {d?.contactName && (
              <>
                <dt>Referente</dt>
                <dd>{d.contactName}</dd>
              </>
            )}
            {d?.source && (
              <>
                <dt>Chi lo porta</dt>
                <dd>{d.source}</dd>
              </>
            )}
            {d && d.services.length > 0 && (
              <>
                <dt>Cosa vuole</dt>
                <dd>{d.services.map((s) => s.name).join(', ')}</dd>
              </>
            )}
          </dl>
          {(d?.notes || main?.notes) && (
            <div className="co-notes">
              <span className="eyebrow-s">Note</span>
              {d?.notes && <div className="co-text">{d.notes}</div>}
              {main?.notes && main.notes !== d?.notes && <div className="co-text muted">{main.notes}</div>}
            </div>
          )}
        </>
      )}

      {tab === 'persone' && (
        <div className="ag-list">
          {c.contacts.map((p) => (
            <div key={p.id} className="ag-row" onClick={() => onPerson(p)}>
              <span className="ag-title">
                <b>{p.name}</b>
                {p.roleTitle && <span className="muted"> · {p.roleTitle}</span>}
                <div className="muted small">{[p.email, p.phone, p.vat ? `P.IVA ${p.vat}` : null].filter(Boolean).join(' · ') || 'Nessun recapito'}</div>
              </span>
              {p.phone && (
                <a className="btn ghost" href={`tel:${p.phone}`} onClick={(e) => e.stopPropagation()} title="Chiama">
                  <Icon name="phone" size={15} />
                </a>
              )}
              {waNumber(p.phone) && <WhatsAppButton ctx={ctx} to={{ name: p.name, phone: p.phone! }} />}
              {p.email && (
                <a className="btn ghost" href={`mailto:${p.email}`} onClick={(e) => e.stopPropagation()} title="Scrivi">
                  <Icon name="mail" size={15} />
                </a>
              )}
            </div>
          ))}
          <button type="button" className="btn ghost co-add" onClick={() => onPerson('new')}>
            <Icon name="plus" size={15} /> Aggiungi una persona
          </button>
        </div>
      )}

      {tab === 'preventivi' &&
        (c.quotes.length === 0 ? (
          <Empty icon="note">Nessun preventivo.</Empty>
        ) : (
          <div className="ag-list">
            {c.quotes.map((x) => (
              <div key={x.id} className="ag-row" onClick={() => go('quotes', { open: x.id })}>
                <span className="ag-time">{x.number}</span>
                <span className="ag-title">
                  {x.subject}
                  <div className="muted small">
                    {QUOTE_LABEL[x.status] ?? x.status} · {x.items.length} voci · creato il {fmtDate(x.createdAt)}
                  </div>
                </span>
                <b className="co-amount">{euro(x.taxableCents)}</b>
              </div>
            ))}
          </div>
        ))}

      {tab === 'documenti' &&
        (d ? (
          <DealFiles ctx={ctx} dealId={d.id} />
        ) : (
          <Empty icon="folder">
            I documenti si allegano alla trattativa.{' '}
            {c.clientId && isAdmin && (
              <button type="button" className="btn small" title="I file condivisi con il cliente" onClick={() => go('documents')}>
                Documenti del cliente
              </button>
            )}
          </Empty>
        ))}

      {tab === 'email' && <MailTab ctx={ctx} c={c} />}

      {tab === 'visite' &&
        (c.visits.length === 0 ? (
          <Empty icon="mic">Nessuna visita.</Empty>
        ) : (
          <div className="ag-list">
            {c.visits.map((v) => (
              <div key={v.id} className={`ag-row visit${v.people.length > 1 ? ' together' : ''}`} onClick={() => go('visits', { open: v.id })}>
                <span className="ag-time">{fmtDateTime(v.startsAt)}</span>
                <span className="ag-title">
                  {v.title}
                  <div className="muted small">{v.people.map((p) => p.name.split(' ')[0]).join(' e ')}</div>
                </span>
              </div>
            ))}
          </div>
        ))}

      {tab === 'fatture' &&
        (c.invoices.length === 0 ? (
          <Empty icon="euro">Nessuna fattura.</Empty>
        ) : (
          <div className="ag-list">
            {c.invoices.map((i) => (
              <div key={i.id} className="ag-row" onClick={() => go('invoices', { open: i.id })}>
                <span className="ag-time">{i.number ?? 'Bozza'}</span>
                <span className="ag-title">
                  {i.subject}
                  <div className="muted small">
                    {INVOICE_LABEL[i.status] ?? i.status}
                    {i.issueDate ? ` · ${fmtDate(i.issueDate)}` : ''}
                    {i.paidAt ? ` · pagata il ${fmtDate(i.paidAt)}` : ''}
                  </div>
                </span>
                <b className="co-amount">{euro(i.totalCents)}</b>
              </div>
            ))}
          </div>
        ))}

      {tab === 'assistenza' &&
        (c.tickets.length === 0 ? (
          <Empty icon="chat">Nessuna richiesta di assistenza.</Empty>
        ) : (
          <div className="ag-list">
            {c.tickets.map((t) => (
              <div key={t.id} className="ag-row" onClick={() => go('tickets')}>
                <span className="ag-time">{fmtDate(t.createdAt)}</span>
                <span className="ag-title">{t.subject}</span>
                <span className="muted small">{TICKET_LABEL[t.status] ?? t.status}</span>
              </div>
            ))}
          </div>
        ))}
    </div>
  );
}

/** Email scambiate con l'azienda: cerca il dominio aziendale (o l'indirizzo, o il nome) nella posta in arrivo e inviata. */
function MailTab({ ctx, c }: { ctx: Ctx; c: Company }) {
  const emails = [c.deal?.email, ...c.contacts.map((p) => p.email)];
  const term = mailTermFor(emails, c.name);
  const [list, setList] = useState<FoundMail[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setList(null);
    setError(null);
    searchMail(ctx.api, term)
      .then((r) => alive && setList(r))
      .catch((e) => alive && setError((e as Error).message));
    return () => {
      alive = false;
    };
  }, [ctx.api, term]);

  if (error)
    return (
      <Empty icon="mail">
        La posta non è raggiungibile: {error}{' '}
        <button type="button" className="btn small" onClick={() => ctx.setPage('mail')}>
          Apri Posta
        </button>
      </Empty>
    );
  return (
    <div className="co-mail">
      <div className="muted small">
        Email con «{term}», in arrivo e inviate.{' '}
        <button type="button" className="link-btn" onClick={() => ctx.setPage('mail', { search: term })}>
          Cerca nella Posta
        </button>
      </div>
      {!list ? (
        <div className="muted small">Cerco nella posta…</div>
      ) : list.length === 0 ? (
        <Empty icon="mail">Nessuna email trovata.</Empty>
      ) : (
        <div className="ag-list">
          {list.slice(0, 40).map((m) => (
            <div key={`${m.folder}-${m.uid}`} className={`ag-row${m.seen ? '' : ' unread'}`} onClick={() => ctx.setPage('mail', { search: term, folder: m.folder, openUid: m.uid })}>
              <span className={`co-dir ${m.folderKind}`} title={m.folderKind === 'sent' ? 'Inviata' : 'Ricevuta'}>
                <Icon name={m.folderKind === 'sent' ? 'send' : 'inbox'} size={14} />
              </span>
              <span className="ag-title">
                {m.subject || '(senza oggetto)'}
                <div className="muted small">
                  {m.folderKind === 'sent' ? `a ${m.to.map((t) => t.name || t.address).join(', ')}` : `da ${m.from.name || m.from.address}`}
                  {m.hasAttachments ? ' · con allegati' : ''}
                </div>
              </span>
              <span className="muted small co-amount">{m.date ? fmtDate(m.date) : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * «Diventa cliente»: un clic quando l'azienda firma. Crea la scheda tra i Clienti, la collega alla trattativa
 * (che diventa «vinta») e da lì si contano ore, fatture e incassi.
 */
function BecomeClientModal({ ctx, c, onClose, onDone }: { ctx: Ctx; c: Company; onClose: () => void; onDone: () => void }) {
  const { api } = ctx;
  const d = c.deal;
  const main = mainContact(c);
  const phones = [...new Set([d?.phone, ...c.contacts.map((p) => p.phone)].filter((x): x is string => !!x))].join(', ');
  const byAgent = !!d && d.ownerId !== ctx.me.user.id;
  const [name, setName] = useState(c.name);
  const [rate, setRate] = useState('');
  // Chi segue (gestione) e chi ha segnalato (segnalazione) possono essere diversi: es. Carves la porta Ferrone e la segue Luca
  const [referral, setReferral] = useState(byAgent && /^commerciale/i.test(d?.source ?? ''));
  const [manage, setManage] = useState(byAgent);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) return setError('Indica il nome del cliente');
    const rateCents = rate.trim() ? parseEuro(rate) : 0;
    if (rateCents == null) return setError('Tariffa oraria non valida');
    const today = new Date().toLocaleDateString('it-IT');
    const input: ClientInput = {
      name: name.trim(),
      notes: [`Diventato cliente il ${today}${d ? ` dalla trattativa «${d.name}»` : ''}.`, main?.vat ? `P.IVA ${main.vat}` : null, main?.address ?? null]
        .filter(Boolean)
        .join(String.fromCharCode(10)),
      phones: phones || null,
      hourlyRateCents: rateCents,
      referrerUserId: referral && d ? d.ownerId : null,
      referralPct: referral ? 12 : 0,
      managerUserId: manage && d ? d.ownerId : null,
      managementPct: manage ? 5 : 0,
      active: true,
    };
    setBusy(true);
    setError(null);
    try {
      const client = await api.post<ClientDto>('/api/admin/clients', input);
      if (d) await api.post(`/api/deals/${d.id}/client`, { clientId: client.id });
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Diventa cliente"
      onClose={onClose}
      footer={
        <div className="quote-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void save()} disabled={busy}>
            <Icon name="check" size={15} /> {busy ? 'Un attimo…' : 'Conferma: è cliente'}
          </button>
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <p className="small" title="Preventivi, documenti e note restano dove sono.">
        <b>{c.name}</b> passa tra i <b>Clienti</b> e la trattativa diventa «vinta».
      </p>
      <Field label="Nome del cliente">
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Tariffa oraria per l'assistenza (€/ora)" hint="Solo se lavori a ore per loro, altrimenti lascia vuoto (si cambia anche dopo).">
        <input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Per esempio 60,00" inputMode="decimal" />
      </Field>
      {byAgent && (
        <>
          <label className="ag-allday" style={{ height: 'auto', marginBottom: 6 }}>
            <input type="checkbox" checked={referral} onChange={(e) => setReferral(e.target.checked)} /> {d!.ownerName} l'ha segnalata: provvigione del 12%
          </label>
          <label className="ag-allday" style={{ height: 'auto', marginBottom: 10 }}>
            <input type="checkbox" checked={manage} onChange={(e) => setManage(e.target.checked)} /> {d!.ownerName} la segue: provvigione del 5% per la gestione
          </label>
          {d?.source && <p className="muted small">Chi l'ha portata: {d.source}</p>}
        </>
      )}
      {phones && <p className="muted small">Numeri collegati per il conteggio delle chiamate: {phones}</p>}
    </Modal>
  );
}
